/**
 * Measurement and gestures for the floating browser preview's shell.
 *
 * Ported from t3code (`ThreadPreviewMiniPlayer.tsx`, `MiniPlayerShell`, MIT,
 * pin `e3e7cc3f`), split out of the shell component to keep each file under
 * the size ratchet.
 *
 * @module useBrowserMiniPlayerGestures
 */
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { type PointerEvent as ReactPointerEvent, useRef } from "react";

import type { BrowserViewportResizeDirection } from "~/browser/browserViewportLayout";

import {
  type BrowserMiniPlayerFrame,
  type BrowserMiniPlayerObstacles,
  clampBrowserMiniPlayerPosition,
  resizeBrowserMiniPlayer,
} from "./browserMiniPlayerLayout";
import { useBrowserMiniPlayerStore, type BrowserMiniPlayerSize } from "./browserMiniPlayerStore";

export type PointerEventHandler = (event: ReactPointerEvent<HTMLElement>) => void;

interface PointerGesture {
  readonly pointerId: number;
  readonly pointerX: number;
  readonly pointerY: number;
  readonly frame: BrowserMiniPlayerFrame;
  readonly direction: BrowserViewportResizeDirection | null;
}

export interface BrowserMiniPlayerShellLayout {
  readonly container: BrowserMiniPlayerSize;
  readonly obstacles: BrowserMiniPlayerObstacles;
}

export const sameBrowserMiniPlayerLayout = (
  a: BrowserMiniPlayerShellLayout,
  b: BrowserMiniPlayerShellLayout,
) =>
  a.container.width === b.container.width &&
  a.container.height === b.container.height &&
  (a.obstacles.composer === b.obstacles.composer ||
    (a.obstacles.composer !== null &&
      b.obstacles.composer !== null &&
      a.obstacles.composer.left === b.obstacles.composer.left &&
      a.obstacles.composer.right === b.obstacles.composer.right &&
      a.obstacles.composer.height === b.obstacles.composer.height));

/**
 * Measures the chat column and the composer in the column's coordinates. The
 * composer's columns come from its centered stack, not the full-width dock,
 * so the margins beside it stay open to the player.
 */
export function measureBrowserMiniPlayerLayout(
  container: HTMLElement,
): BrowserMiniPlayerShellLayout {
  const root = container.closest<HTMLElement>('[data-slot="sidebar-inset"]') ?? container;
  const stack = root.querySelector<HTMLElement>('[data-chat-composer-stack="true"]');
  const dock = stack?.closest<HTMLElement>('[data-chat-composer-dock="true"]');
  const containerRect = container.getBoundingClientRect();
  const stackRect = stack?.getBoundingClientRect();
  const dockRect = dock?.getBoundingClientRect();
  return {
    container: { width: container.clientWidth, height: container.clientHeight },
    obstacles: {
      composer:
        stackRect &&
        dockRect &&
        dockRect.height > 0 &&
        stackRect.left >= containerRect.left &&
        stackRect.right <= containerRect.right
          ? {
              left: Math.floor(stackRect.left - containerRect.left),
              right: Math.ceil(stackRect.right - containerRect.left),
              height: Math.ceil(dockRect.height),
            }
          : null,
    },
  };
}

/**
 * Drag and resize gestures for the floating frame, in container coordinates.
 * A drag on the pill moves the frame; a drag on a resize handle resizes from
 * that edge while the store's `sourceKey` guards against a stale gesture
 * outliving the source it started on.
 */
export function useBrowserMiniPlayerGestures(input: {
  readonly threadRef: ScopedThreadRef;
  readonly sourceKey: string;
  readonly frame: BrowserMiniPlayerFrame | null;
  readonly container: BrowserMiniPlayerSize | null;
  readonly obstacles: BrowserMiniPlayerObstacles;
  readonly sourceSize: BrowserMiniPlayerSize;
}) {
  const gestureRef = useRef<PointerGesture | null>(null);

  const beginGesture = (
    event: ReactPointerEvent<HTMLElement>,
    direction: BrowserViewportResizeDirection | null = null,
  ) => {
    if (event.button !== 0 || !input.frame) return;
    gestureRef.current = {
      pointerId: event.pointerId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      frame: input.frame,
      direction,
    };
    if (typeof event.currentTarget.setPointerCapture === "function") {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerMove: PointerEventHandler = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId || !input.container) return;
    const delta = { x: event.clientX - gesture.pointerX, y: event.clientY - gesture.pointerY };
    const store = useBrowserMiniPlayerStore.getState();
    if (gesture.direction === null) {
      store.move(
        input.threadRef,
        input.sourceKey,
        clampBrowserMiniPlayerPosition(
          { x: gesture.frame.x + delta.x, y: gesture.frame.y + delta.y },
          input.container,
          gesture.frame,
          input.obstacles,
        ),
      );
      return;
    }
    const next = resizeBrowserMiniPlayer({
      start: gesture.frame,
      direction: gesture.direction,
      delta,
      source: input.sourceSize,
      container: input.container,
      obstacles: input.obstacles,
    });
    store.resize(input.threadRef, input.sourceKey, next.width);
    store.move(input.threadRef, input.sourceKey, { x: next.x, y: next.y });
  };

  const endGesture: PointerEventHandler = (event) => {
    if (gestureRef.current?.pointerId !== event.pointerId) return;
    gestureRef.current = null;
    if (
      typeof event.currentTarget.hasPointerCapture === "function" &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return { beginGesture, handlePointerMove, endGesture };
}
